import { useCallback, useEffect } from 'react';
import { createStore, createHook } from 'react-sweet-state';
import type { JWMView } from '@atlassian/jira-business-common';

type State = {
	[view: string]: {
		shouldExpandAllItems: boolean;
		shouldCollapseAllItems: boolean;
		hasExpandedAllItems: boolean;
		hasCollapsedAllItems: boolean;
	};
};

const actions = {
	toggleExpanding:
		(view: string) =>
		({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				[view]: {
					...state[view],
					shouldExpandAllItems: !state[view].shouldExpandAllItems,
					shouldCollapseAllItems: false,
				},
			});
		},
	setCollapsing:
		(view: string) =>
		({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				[view]: {
					...state[view],
					shouldExpandAllItems: false,
					shouldCollapseAllItems: true,
				},
			});
		},
	disableExpandCollapse:
		(view: string) =>
		({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				[view]: {
					...state[view],
					shouldExpandAllItems: false,
					shouldCollapseAllItems: false,
				},
			});
		},
	setHasExpandedAllItems:
		(view: string, hasExpanded: boolean) =>
		({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				[view]: {
					...state[view],
					hasExpandedAllItems: hasExpanded,
				},
			});
		},
	setHasCollapsedAllItems:
		(view: string, hasCollapsed: boolean) =>
		({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				[view]: {
					...state[view],
					hasCollapsedAllItems: hasCollapsed,
				},
			});
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	initialState: {
		list: {
			shouldExpandAllItems: false,
			shouldCollapseAllItems: false,
			hasExpandedAllItems: false,
			hasCollapsedAllItems: false,
		},
		timeline: {
			shouldExpandAllItems: false,
			shouldCollapseAllItems: false,
			hasExpandedAllItems: false,
			hasCollapsedAllItems: false,
		},
	},
	actions,
	name: 'JWMExpandCollapseStore',
});

const useHook = createHook(Store);

type UseExpandCollapseAllItemsProps = {
	view: JWMView;
	onCollapse?: () => void;
};

export const useExpandCollapseAllItems = ({ view, onCollapse }: UseExpandCollapseAllItemsProps) => {
	const [
		state,
		{
			toggleExpanding,
			setCollapsing,
			disableExpandCollapse,
			setHasExpandedAllItems,
			setHasCollapsedAllItems,
		},
	] = useHook();
	const {
		shouldExpandAllItems,
		shouldCollapseAllItems,
		hasExpandedAllItems,
		hasCollapsedAllItems,
	} = state[view];

	const toggleExpandingForView = useCallback(() => toggleExpanding(view), [toggleExpanding, view]);
	const setCollapsingForView = useCallback(() => setCollapsing(view), [setCollapsing, view]);
	const setHasCollapsedAllItemsForView = useCallback(
		(hasCollapsed: boolean) => setHasCollapsedAllItems(view, hasCollapsed),
		[setHasCollapsedAllItems, view],
	);
	const setHasExpandedAllItemsForView = useCallback(
		(hasExpanded: boolean) => setHasExpandedAllItems(view, hasExpanded),
		[setHasExpandedAllItems, view],
	);
	const disableExpandCollapseForView = useCallback(
		() => disableExpandCollapse(view),
		[disableExpandCollapse, view],
	);

	useEffect(() => {
		if (onCollapse && shouldCollapseAllItems) {
			onCollapse();
			disableExpandCollapseForView();
		}
	}, [disableExpandCollapseForView, onCollapse, shouldCollapseAllItems]);

	return [
		{
			shouldExpandAllItems,
			shouldCollapseAllItems,
			hasExpandedAllItems,
			hasCollapsedAllItems,
		},
		{
			toggleExpandAllItems: toggleExpandingForView,
			collapseAllItems: setCollapsingForView,
			disableExpandCollapseAllItems: disableExpandCollapseForView,
			setHasExpandedAllItems: setHasExpandedAllItemsForView,
			setHasCollapsedAllItems: setHasCollapsedAllItemsForView,
		},
	] as const;
};
